import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Menu as MantineMenu } from "@mantine/core";
import { User, LogOut, Ticket, CreditCard } from "lucide-react";
import {
  useUser,
  getOrgTokenKey,
  getOrgUserProfileKey,
} from "../../context/UserContext";
import { ProfileModal } from "../ProfileModal";
import {
  Avatar as CustomAvatar,
  AvatarImage,
  AvatarFallback,
} from "../ui/avatar";
import { getMyTicketsUrl, getPaymentHistoryUrl } from "../../lib/utils/domain";
import { TheDRALogo } from "./TheDRALogo";
import "./styles.scss";

interface TheDRANavbarProps {
  onLoginClick?: () => void;
  showHomeLink?: boolean; // For event pages, we might want to link to homepage
  homepageUrl?: string; // URL to homepage for navigation
  organizationSlug?: string; // Organization slug to check authentication
  navLinks?: NavLink[];
}

export interface NavLink {
  label: string;
  href: string;
  external?: boolean;
}

const TheDRANavbar: React.FC<TheDRANavbarProps> = ({
  onLoginClick,
  showHomeLink = true,
  homepageUrl,
  organizationSlug = "thedra",
  navLinks,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const { userProfile, isAuthenticated, clearUserProfile, setUserProfile } =
    useUser();

  // Check if user is logged in for this specific organization
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);

  useEffect(() => {
    const checkAuth = () => {
      const tokenKey = getOrgTokenKey(organizationSlug);
      const profileKey = getOrgUserProfileKey(organizationSlug);
      const token = localStorage.getItem(tokenKey);
      const profileStr = localStorage.getItem(profileKey);

      if (token && profileStr) {
        try {
          const profile = JSON.parse(profileStr);
          // Make sure this profile matches the organization
          if (
            profile.organizationSlug === organizationSlug ||
            organizationSlug === "thedra"
          ) {
            setIsLoggedIn(true);
            setCurrentUserProfile(profile);
            // Sync with UserContext if not already set
            if (
              !userProfile ||
              userProfile.organizationSlug !== organizationSlug
            ) {
              setUserProfile(profile);
            }
          } else {
            setIsLoggedIn(false);
            setCurrentUserProfile(null);
          }
        } catch (error) {
          setIsLoggedIn(false);
          setCurrentUserProfile(null);
        }
      } else {
        setIsLoggedIn(false);
        setCurrentUserProfile(null);
      }
    };

    checkAuth();
    // Also check if userProfile from context matches
    if (userProfile && userProfile.organizationSlug === organizationSlug) {
      setIsLoggedIn(true);
      setCurrentUserProfile(userProfile);
    }

    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, [organizationSlug, userProfile, setUserProfile]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string
  ) => {
    e.preventDefault();

    // If we're on a different page (like event page), navigate to homepage first
    if (homepageUrl && window.location.pathname !== homepageUrl) {
      navigate(homepageUrl + targetId);
      return;
    }

    if (targetId === "#home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const element = document.getElementById(targetId.replace("#", ""));
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleLogout = () => {
    clearUserProfile(organizationSlug);
    setIsLoggedIn(false);
    setCurrentUserProfile(null);
    window.location.reload();
  };

  const getInitials = (firstName: string, lastName: string) => {
    return (
      `${firstName?.charAt(0) || ""}${
        lastName?.charAt(0) || ""
      }`.toUpperCase() || "U"
    );
  };

  const displayProfile = currentUserProfile || userProfile;
  const isUserLoggedIn =
    isLoggedIn ||
    (isAuthenticated && userProfile?.organizationSlug === organizationSlug);

  const resolvedNavLinks: NavLink[] = useMemo(() => {
    const defaultLinks: NavLink[] = [
      ...(showHomeLink ? [{ label: "Home", href: "#home" }] : []),
      { label: "About", href: "#about-section" },
      { label: "Events", href: "#events" },
    ];

    if (navLinks && navLinks.length > 0) {
      return navLinks;
    }

    return defaultLinks;
  }, [navLinks, showHomeLink]);

  const handleMenuClose = () => setIsMenuOpen(false);

  const renderLink = (link: NavLink, idx: number, closeAfterClick = false) => {
    if (link.external) {
      return (
        <a
          key={`${link.label}-${idx}`}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label}
        </a>
      );
    }

    return (
      <a
        key={`${link.label}-${idx}`}
        href={link.href}
        onClick={(e) => {
          handleNavClick(e, link.href);
          if (closeAfterClick) {
            handleMenuClose();
          }
        }}
      >
        {link.label}
      </a>
    );
  };

  const loginTrigger =
    isUserLoggedIn && displayProfile ? (
      <MantineMenu shadow="md" width={200} position="bottom-end">
        <MantineMenu.Target>
          <button
            className="thedra-user-menu-trigger"
            aria-label="Account menu"
          >
            <CustomAvatar className="thedra-user-avatar">
              {displayProfile.user?.image ? (
                <AvatarImage
                  src={displayProfile.user.image}
                  alt={`${displayProfile.user.first_name} ${displayProfile.user.last_name}`}
                />
              ) : null}
              <AvatarFallback className="thedra-avatar-fallback">
                {getInitials(
                  displayProfile.user?.first_name || "",
                  displayProfile.user?.last_name || ""
                )}
              </AvatarFallback>
            </CustomAvatar>
          </button>
        </MantineMenu.Target>

        <MantineMenu.Dropdown>
          <MantineMenu.Label>
            {displayProfile.user?.first_name} {displayProfile.user?.last_name}
          </MantineMenu.Label>
          <MantineMenu.Item
            leftSection={<User className="w-4 h-4" />}
            onClick={() => setShowProfileModal(true)}
          >
            Profile
          </MantineMenu.Item>
          <MantineMenu.Item
            leftSection={<Ticket className="w-4 h-4" />}
            onClick={() => navigate(getMyTicketsUrl(organizationSlug))}
          >
            My Tickets
          </MantineMenu.Item>
          <MantineMenu.Item
            leftSection={<CreditCard className="w-4 h-4" />}
            onClick={() => navigate(getPaymentHistoryUrl(organizationSlug))}
          >
            Payment History
          </MantineMenu.Item>
          <MantineMenu.Divider />
          <MantineMenu.Item
            color="red"
            leftSection={<LogOut className="w-4 h-4" />}
            onClick={handleLogout}
          >
            Logout
          </MantineMenu.Item>
        </MantineMenu.Dropdown>
      </MantineMenu>
    ) : (
      <button
        type="button"
        className="thedra-login-button"
        onClick={(e) => {
          e.preventDefault();
          if (onLoginClick) {
            onLoginClick();
          }
        }}
      >
        Log In
      </button>
    );

  return (
    <header className="thedra-header">
      <div className="thedra-container">
        <div className="thedra-header-content">
          <div className="thedra-logo">
            <TheDRALogo />
          </div>

          <button
            className={`thedra-menu-toggle ${isMenuOpen ? "open" : ""}`}
            aria-label="Toggle navigation menu"
            onClick={() => setIsMenuOpen((prev) => !prev)}
          >
            <span />
            <span />
            <span />
          </button>

          <nav className={`thedra-nav ${isMenuOpen ? "open" : ""}`}>
            <div className="thedra-nav-links">
              {resolvedNavLinks.map((link, idx) => renderLink(link, idx))}
            </div>
          </nav>
          <div className="thedra-header-actions">{loginTrigger}</div>
        </div>
      </div>

      <div className={`thedra-mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <div className="thedra-mobile-menu-content">
          <div className="thedra-mobile-links">
            {resolvedNavLinks.map((link, idx) =>
              renderLink(link, idx, true /* closeAfterClick */)
            )}
          </div>
          <div className="thedra-mobile-actions">{loginTrigger}</div>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && displayProfile && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
          user={displayProfile.user}
          organization={displayProfile.organization}
        />
      )}
    </header>
  );
};

export default TheDRANavbar;
